/**
 * The type and spacing scale the response prose is built from.
 *
 * Split out of `response-prose` because the two do different jobs: this is the
 * per-density table of sizes and margins, that is the component map which
 * consumes it. The invariants binding these values to the thread rungs are
 * enforced in `response-thread.test.ts`, which reads both.
 */

/**
 * Which scale a surface renders at. A ROLE, not a viewport width: `comfortable`
 * is a primary reading surface, `compact` a reference panel beside one.
 */
export type ProseDensity = 'comfortable' | 'compact';

export const DENSITIES = [
  'comfortable',
  'compact',
] as const satisfies readonly ProseDensity[];

/**
 * Per-density type sizes.
 *
 * `comfortable` is a primary reading surface — a full page, at any device size,
 * including full-screen mobile. `compact` is a reference panel alongside the
 * user's real work. That distinction is the one that matters, and it is NOT the
 * same as "narrow": a phone is a narrow viewport but it is still the primary
 * reading surface, so it stays comfortable. Sizing on viewport width would get
 * that case backwards, and sizing on container width would too.
 *
 * Headings compress HARDER than body between the two presets (24→20 against
 * 16→14). A narrow panel has less visual competition, so hierarchy needs less
 * absolute contrast to read; keeping the 24px heading over a 14px body would
 * make the step jarring in a ~380px column.
 *
 * 14px is the floor. Below it, body copy stops being comfortable for a long
 * answer, and the panels these sizes serve are exactly where users read most.
 *
 * ## Six markdown levels, two visual tiers
 *
 * Markdown gives the model six heading levels and it uses them inconsistently —
 * the same kind of thing came back as `h4` in one answer and `h3` in the next,
 * which is why "Set up" and "A. Escalation" did not look like peers. Six
 * distinct sizes could not read as six levels in a 380px column anyway, so the
 * ladder collapses onto two tiers a reader can actually tell apart:
 *
 * | tier    | levels | treatment                    |
 * | ------- | ------ | ---------------------------- |
 * | section | h1, h2 | Exposure semibold, 24/20px   |
 * | label   | h3-h6  | UI font, body size, semibold |
 *
 * The label tier deliberately stops being a "heading" in appearance: at body
 * size in the UI font it reads as a bold lead-in to the block under it, which
 * is what the model is actually using these levels for.
 *
 * THREE tiers was the previous shape and it failed. Section and subsection were
 * both Exposure at normal weight, separated by size alone — two pixels apart in
 * compact. Once `response-headings` began keying tiers off outline depth those
 * two became the tiers in nearly every answer, so the least legible distinction
 * carried almost all the work, while the one with real contrast (sans against
 * Exposure) sat at a depth answers rarely reach. Two tiers differ by FAMILY, so
 * no size comparison is needed to tell them apart.
 *
 * `meta` is always one rung below `body` in the same column. See
 * docs/response-rendering/README.md.
 */
export const DENSITY_TYPE = {
  comfortable: {
    h1: 'text-2xl',
    h2: 'text-2xl',
    h3: 'text-base',
    h4: 'text-base',
    h5: 'text-base',
    h6: 'text-base',
    body: 'text-base',
    meta: 'text-sm',
  },
  compact: {
    h1: 'text-xl',
    h2: 'text-xl',
    h3: 'text-sm',
    h4: 'text-sm',
    h5: 'text-sm',
    h6: 'text-sm',
    body: 'text-sm',
    meta: 'text-xs',
  },
} as const satisfies Record<ProseDensity, Record<string, string>>;

/**
 * Per-density block margins, one value per visual tier.
 *
 * Two rules bind these to the thread scale in `response-thread`, and
 * `response-thread.test.ts` fails if either breaks:
 *
 *   - a section heading never opens more than `questionToAnswer` (44 / 32)
 *   - nothing here ever opens more than `turnToTurn` (52 / 40)
 *
 * Both used to be violated. A comfortable `h1` opened `sm:!mt-16` — 64px —
 * against 48px for a whole new exchange, so a heading mid-answer read as a
 * bigger break than a new question, and the thread had no outer hierarchy left
 * to spend. The `sm:` steps are gone with it, since the density preset already
 * encodes the surface and a second viewport-keyed step just reintroduced the
 * thing density replaced.
 *
 * The ceiling is what sizes this table. `turnToTurn` is two body line boxes
 * (see `response-thread`), so the three rungs below it — paragraph, label,
 * section — have to fit underneath in distinguishable steps.
 *
 * Every value is a simple fraction of the body line box (24 / 20), which is
 * what the thread rungs are whole multiples of: list 1/2, paragraph 2/3, label
 * 1, section 3/2 (5/3 at comfortable since the lift described on
 * `DENSITY_SPACING`). Reach for a bigger heading margin and the thing to change is
 * the thread ceiling, not this table in isolation — the section tier already
 * sits at the top of what invariant 1 permits, which is why lifting the
 * comfortable section tier to 40px lifted `questionToAnswer` to 44px with it.
 * The invariants exist to force that conversation, and they did.
 *
 * h1/h2 share a value and h3/h4/h5/h6 share a value because they are one tier —
 * see `DENSITY_TYPE`.
 *
 * ## Every value is a TOP margin. No exceptions, and that is load-bearing
 *
 * A block owns the gap ABOVE itself and never below, so the separation at any
 * boundary is one number: the lower block's top margin. `blockquote`, `table`
 * and `hr` used to be the exceptions, on `!my-*`, and the exception is what hid
 * a defect from every invariant in `response-thread.test.ts`:
 *
 *   - an `hr` carried `!my-6`, so it opened 24px above AND below. That is 48px
 *     of separation around one rule — EQUAL to `turnToTurn`, which invariant 2
 *     exists to keep as the largest gap in a thread. A divider inside one
 *     answer separated as hard as two different exchanges. Compact was the same
 *     story at 40px.
 *   - `blockquote` reached 40 / 32px the same way, landing exactly on
 *     `questionToAnswer`.
 *
 * The invariants could not catch either, because they read `!mt-` tokens and a
 * two-sided element states half its separation in a token they never looked at.
 * Now that nothing carries a bottom margin, what a test measures and what a
 * reader sees are the same number, collapsing rules do not enter into it, and
 * the assertion covers this table exhaustively rather than heading by heading.
 *
 * Values are unchanged by that conversion — only the bottom halves are gone.
 */
export const DENSITY_SPACING = {
  comfortable: {
    // 40px: the section break a reader actually sees between `h2` sections on
    // the primary reading surface. It cannot exceed question-to-answer
    // (invariant 1), so this value and that rung move together — lifting this
    // from 36px is why question-to-answer went to 44px and turn-to-turn to 52px,
    // and why the ratio bands in `response-thread.test.ts` were widened.
    h1: '!mt-10',
    h2: '!mt-10',
    h3: '!mt-6',
    h4: '!mt-6',
    h5: '!mt-6',
    h6: '!mt-6',
    paragraph: '!mt-4',
    list: '!mt-3',
    listItem: '!mt-1',
    blockquote: '!mt-5',
    table: '!mt-4',
    hr: '!mt-6',
  },
  compact: {
    h1: '!mt-7',
    h2: '!mt-7',
    h3: '!mt-5',
    h4: '!mt-5',
    h5: '!mt-5',
    h6: '!mt-5',
    paragraph: '!mt-3',
    list: '!mt-2',
    listItem: '!mt-1',
    blockquote: '!mt-4',
    table: '!mt-3',
    hr: '!mt-5',
  },
} as const satisfies Record<ProseDensity, Record<string, string>>;

/** Every markdown heading level, in order. Two visual tiers — see `DENSITY_TYPE`. */
export const HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;

export type ProseHeadingLevel = (typeof HEADING_LEVELS)[number];
